import { useState, useCallback, useRef, useEffect } from "react";
import WorldMap, { regions } from "react-svg-worldmap";
import type { CountryContext } from "react-svg-worldmap";

/* Zone kleuren (huisstijl) */
const ZONE_COLORS: Record<number, string> = {
  1: "#eb4f05",
  2: "#0090e3",
  3: "#1fa34a",
  4: "#f15656",
};

/* Zone tarieven */
const ZONE_TARIEVEN: Record<number, { name: string; start: string; vast: string; mobiel: string }> = {
  1: { name: "Zone 1", start: "€ 0,04", vast: "€ 0,07", mobiel: "€ 0,27" },
  2: { name: "Zone 2", start: "€ 0,04", vast: "€ 0,34", mobiel: "€ 0,50" },
  3: { name: "Zone 3", start: "€ 0,04", vast: "€ 0,62", mobiel: "€ 0,80" },
  4: { name: "Zone 4", start: "€ 0,04", vast: "€ 5,00", mobiel: "€ 5,00" },
};

/* Country name → ISO-alpha2 mapping voor alle landen in de zones */
const NAME_TO_ISO: Record<string, string> = {
  // Zone 1
  "Andorra": "ad", "Austria": "at", "Belgium": "be", "Bulgaria": "bg",
  "Canada": "ca", "China": "cn", "Croatia": "hr", "Cyprus": "cy",
  "Czech Republic": "cz", "Denmark": "dk", "Estonia": "ee", "Finland": "fi",
  "France": "fr", "Germany": "de", "Greece": "gr", "Hungary": "hu",
  "Iceland": "is", "India": "in", "Ireland": "ie", "Italy": "it",
  "Latvia": "lv", "Lithuania": "lt", "Luxembourg": "lu", "Malta": "mt",
  "Norway": "no", "Poland": "pl", "Portugal": "pt", "Romania": "ro",
  "Slovakia": "sk", "Slovenia": "si", "Spain": "es", "Sweden": "se",
  "Switzerland": "ch", "United Kingdom": "gb", "United States": "us",
  // Zone 2
  "American Samoa": "as", "Argentina": "ar", "Aruba": "aw", "Australia": "au",
  "Brazil": "br", "Cambodia": "kh", "Chile": "cl", "Colombia": "co",
  "Costa Rica": "cr", "Curaçao": "cw", "Dominican Republic": "do",
  "El Salvador": "sv", "Faroe Islands": "fo", "Gibraltar": "gi",
  "Guadeloupe": "gp", "Honduras": "hn", "Hong Kong": "hk", "Indonesia": "id",
  "Israel": "il", "Jamaica": "jm", "Japan": "jp", "Korea (South)": "kr",
  "Liechtenstein": "li", "Malaysia": "my", "Martinique": "mq", "Mexico": "mx",
  "Monaco": "mc", "New Zealand": "nz", "Nicaragua": "ni", "Palestine": "ps",
  "Panama": "pa", "Peru": "pe", "Philippines": "ph", "Puerto Rico": "pr",
  "Russia": "ru", "San Marino": "sm", "Singapore": "sg", "South Africa": "za",
  "Sri Lanka": "lk", "Suriname": "sr", "Taiwan": "tw", "Thailand": "th",
  "Turkey": "tr", "Ukraine": "ua", "United Arab Emirates": "ae", "Uruguay": "uy",
  // Zone 3
  "Afghanistan": "af", "Albania": "al", "Algeria": "dz", "Angola": "ao",
  "Antigua & Barbuda": "ag", "Armenia": "am", "Azerbaijan": "az",
  "Bahamas": "bs", "Bahrain": "bh", "Bangladesh": "bd", "Barbados": "bb",
  "Belarus": "by", "Belize": "bz", "Benin": "bj", "Bermuda": "bm",
  "Bhutan": "bt", "Bolivia": "bo", "Bosnia": "ba", "Botswana": "bw",
  "Brunei": "bn", "Burkina Faso": "bf", "Burundi": "bi", "Cameroon": "cm",
  "Cape Verde Islands": "cv", "Cayman Islands": "ky",
  "Central African Republic": "cf", "Chad": "td", "Comoros": "km",
  "Congo": "cg", "Congo-DR": "cd", "Cuba": "cu", "Djibouti": "dj",
  "Dominica": "dm", "East Timor": "tl", "Ecuador": "ec", "Egypt": "eg",
  "Equatorial Guinea": "gq", "Eritrea": "er", "Ethiopia": "et",
  "Falkland Islands": "fk", "Fiji Island": "fj", "French Polynesia": "pf",
  "Gabon Republic": "ga", "Gambia": "gm", "Georgia": "ge", "Ghana": "gh",
  "Greenland": "gl", "Grenada": "gd", "Guam": "gu", "Guatemala": "gt",
  "Guinea": "gn", "Guinea Bissau": "gw", "Guyana": "gy", "Haiti": "ht",
  "Iran": "ir", "Iraq": "iq", "Ivory Coast": "ci", "Jordan": "jo",
  "Kazakhstan": "kz", "Kenya": "ke", "Korea (North)": "kp", "Kosovo": "xk",
  "Kuwait": "kw", "Kyrgyzstan": "kg", "Laos": "la", "Lebanon": "lb",
  "Lesotho": "ls", "Liberia": "lr", "Libya": "ly", "Macau": "mo",
  "Macedonia": "mk", "Madagascar": "mg", "Malawi": "mw", "Maldives": "mv",
  "Mali": "ml", "Mauritania": "mr", "Mauritius": "mu", "Moldova": "md",
  "Mongolia": "mn", "Montenegro": "me", "Morocco": "ma", "Mozambique": "mz",
  "Myanmar": "mm", "Namibia": "na", "Nepal": "np", "New Caledonia": "nc",
  "Niger": "ne", "Nigeria": "ng", "Oman": "om", "Pakistan": "pk",
  "Papua New Guinea": "pg", "Paraguay": "py", "Qatar": "qa", "Rwanda": "rw",
  "Sao Tome": "st", "Saudi Arabia": "sa", "Senegal": "sn", "Serbia": "rs",
  "Seychelles": "sc", "Sierra Leone": "sl", "Solomon Islands": "sb",
  "Somalia": "so", "South Sudan": "ss", "Sudan": "sd", "Swaziland": "sz",
  "Syria": "sy", "Tajikistan": "tj", "Tanzania": "tz", "Togo": "tg",
  "Trinidad & Tobago": "tt", "Tunisia": "tn", "Turkmenistan": "tm",
  "Uganda": "ug", "Uzbekistan": "uz", "Venezuela": "ve", "Vietnam": "vn",
  "Yemen Arab Republic": "ye", "Zambia": "zm", "Zimbabwe": "zw",
  // Zone 4
  "Cook Islands": "ck", "Kiribati": "ki", "Nauru": "nr", "Tokelau": "tk",
  "Tonga": "to", "Vanuatu": "vu",
};

/* Build zone arrays for isoToZone lookup */
const zoneCountries: { id: number; countries: string[] }[] = [
  {
    id: 1,
    countries: [
      "Andorra", "Austria", "Belgium", "Bulgaria", "Canada", "China", "Croatia", "Cyprus",
      "Czech Republic", "Denmark", "Estonia", "Finland", "France", "Germany", "Greece",
      "Hungary", "Iceland", "India", "Ireland", "Italy", "Latvia", "Lithuania", "Luxembourg",
      "Malta", "Norway", "Poland", "Portugal", "Romania", "Slovakia", "Slovenia", "Spain",
      "Sweden", "Switzerland", "United Kingdom", "United States",
    ],
  },
  {
    id: 2,
    countries: [
      "American Samoa", "Argentina", "Aruba", "Australia", "Brazil", "Cambodia",
      "Chile", "Colombia", "Costa Rica", "Curaçao", "Dominican Republic", "El Salvador",
      "Faroe Islands", "Gibraltar", "Guadeloupe", "Honduras", "Hong Kong", "Indonesia",
      "Israel", "Jamaica", "Japan", "Korea (South)", "Liechtenstein", "Malaysia",
      "Martinique", "Mexico", "Monaco", "New Zealand", "Nicaragua", "Palestine",
      "Panama", "Peru", "Philippines", "Puerto Rico", "Russia", "San Marino",
      "Singapore", "South Africa", "Sri Lanka", "Suriname", "Taiwan", "Thailand",
      "Turkey", "Ukraine", "United Arab Emirates", "Uruguay",
    ],
  },
  {
    id: 3,
    countries: [
      "Afghanistan", "Albania", "Algeria", "Angola", "Antigua & Barbuda", "Armenia",
      "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belize",
      "Benin", "Bermuda", "Bhutan", "Bolivia", "Bosnia", "Botswana", "Brunei",
      "Burkina Faso", "Burundi", "Cameroon", "Cape Verde Islands", "Cayman Islands",
      "Central African Republic", "Chad", "Comoros", "Congo", "Congo-DR", "Cuba",
      "Djibouti", "Dominica", "East Timor", "Ecuador", "Egypt", "Equatorial Guinea",
      "Eritrea", "Ethiopia", "Falkland Islands", "Fiji Island", "French Polynesia",
      "Gabon Republic", "Gambia", "Georgia", "Ghana", "Greenland", "Grenada", "Guam",
      "Guatemala", "Guinea", "Guinea Bissau", "Guyana", "Haiti", "Iran", "Iraq",
      "Ivory Coast", "Jordan", "Kazakhstan", "Kenya", "Korea (North)", "Kosovo",
      "Kuwait", "Kyrgyzstan", "Laos", "Lebanon", "Lesotho", "Liberia", "Libya", "Macau",
      "Macedonia", "Madagascar", "Malawi", "Maldives", "Mali", "Mauritania", "Mauritius",
      "Moldova", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia",
      "Nepal", "New Caledonia", "Niger", "Nigeria", "Oman", "Pakistan",
      "Papua New Guinea", "Paraguay", "Qatar", "Rwanda", "Sao Tome", "Saudi Arabia",
      "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Solomon Islands", "Somalia",
      "South Sudan", "Sudan", "Swaziland", "Syria", "Tajikistan", "Tanzania", "Togo",
      "Trinidad & Tobago", "Tunisia", "Turkmenistan", "Uganda", "Uzbekistan",
      "Venezuela", "Vietnam", "Yemen Arab Republic", "Zambia", "Zimbabwe",
    ],
  },
  {
    id: 4,
    countries: [
      "Cook Islands", "Kiribati", "Nauru", "Tokelau", "Tonga", "Vanuatu",
    ],
  },
];

const isoToZone: Record<string, number> = {};
for (const zone of zoneCountries) {
  for (const country of zone.countries) {
    const iso = NAME_TO_ISO[country];
    if (iso) isoToZone[iso] = zone.id;
  }
}

/* Build ISO → country name lookup from library's regions export */
const isoToName: Record<string, string> = {};
for (const r of regions) {
  isoToName[r.code.toLowerCase()] = r.name;
}

/* Data: every mapped country gets a value = its zone id */
const mapData = Object.entries(isoToZone).map(([iso, zoneId]) => ({
  country: iso,
  value: zoneId,
}));

/* Style function: color each country by zone */
const stylingFunction = ({ countryValue, countryCode }: CountryContext): React.CSSProperties => {
  const zone = (countryValue as number) || isoToZone[countryCode.toLowerCase()];
  if (zone && ZONE_COLORS[zone]) {
    return {
      fill: ZONE_COLORS[zone],
      fillOpacity: 0.85,
      stroke: ZONE_COLORS[zone],
      strokeWidth: 0.5,
      strokeOpacity: 0.5,
      cursor: "pointer",
    };
  }
  return {
    fill: "#e5e5e5",
    fillOpacity: 0.4,
    stroke: "#d4d4d4",
    strokeWidth: 0.3,
    cursor: "default",
  };
};

type TooltipData = {
  x: number;
  y: number;
  countryName: string;
  zone: number;
} | null;

export default function WorldMapZones() {
  const [tooltip, setTooltip] = useState<TooltipData>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  /* Attach native mouseover/mousemove/mouseleave on SVG paths */
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMove = (e: MouseEvent) => {
      const target = e.target as SVGElement;
      if (target.tagName !== "path") {
        setTooltip(null);
        return;
      }

      // Find path index among siblings in the <g> group
      const parent = target.parentElement;
      if (!parent) return;
      const paths = Array.from(parent.querySelectorAll("path"));
      const idx = paths.indexOf(target as SVGPathElement);
      if (idx === -1 || idx >= regions.length) return;

      const region = regions[idx];
      const iso = region.code.toLowerCase();
      const zone = isoToZone[iso];
      if (!zone) {
        setTooltip(null);
        return;
      }

      const rect = container.getBoundingClientRect();
      setTooltip({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
        countryName: region.name,
        zone,
      });
    };

    const handleLeave = () => setTooltip(null);

    container.addEventListener("mousemove", handleMove);
    container.addEventListener("mouseleave", handleLeave);
    return () => {
      container.removeEventListener("mousemove", handleMove);
      container.removeEventListener("mouseleave", handleLeave);
    };
  }, []);

  const t = tooltip && ZONE_TARIEVEN[tooltip.zone] ? ZONE_TARIEVEN[tooltip.zone] : null;

  return (
    <div className="mx-auto mb-10 max-w-5xl flex flex-col items-center">
      <div
        ref={containerRef}
        className="relative w-full [&_figure]:!bg-transparent [&_figure]:!m-0 [&_figure]:!flex [&_figure]:!justify-center [&_text]:!hidden"
      >
        <WorldMap
          color="#eb4f05"
          size="responsive"
          data={mapData}
          styleFunction={stylingFunction}
          tooltipBgColor="transparent"
          tooltipTextColor="transparent"
          backgroundColor="transparent"
        />

        {/* Custom tooltip — always above cursor */}
        {tooltip && t && (
          <div
            className="pointer-events-none absolute z-50 -translate-x-1/2 -translate-y-full"
            style={{ left: tooltip.x, top: tooltip.y - 16 }}
          >
            <div className="rounded-xl bg-[#1a1a2e] px-4 py-3 shadow-xl text-white text-sm min-w-[210px]">
              <div className="flex items-center gap-2 mb-2">
                <span
                  className="h-3 w-3 rounded-full shrink-0"
                  style={{ backgroundColor: ZONE_COLORS[tooltip.zone] }}
                />
                <span className="font-bold text-[15px]">{tooltip.countryName}</span>
              </div>
              <div
                className="text-xs font-semibold mb-1.5 px-1.5 py-0.5 rounded inline-block"
                style={{ backgroundColor: ZONE_COLORS[tooltip.zone] + "30", color: ZONE_COLORS[tooltip.zone] }}
              >
                {t.name}
              </div>
              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs mt-1">
                <span className="text-white/50">Starttarief</span>
                <span className="font-medium text-white">{t.start}</span>
                <span className="text-white/50">Vast</span>
                <span className="font-medium text-white">{t.vast}/min</span>
                <span className="text-white/50">Mobiel</span>
                <span className="font-medium text-white">{t.mobiel}/min</span>
              </div>
            </div>
            {/* Arrow */}
            <div className="flex justify-center -mt-[1px]">
              <div className="h-2 w-2 rotate-45 bg-[#1a1a2e]" />
            </div>
          </div>
        )}
      </div>
      <div className="mt-4 flex flex-wrap justify-center gap-4 text-xs font-medium">
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-full" style={{ backgroundColor: "#eb4f05" }} /> Zone 1
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-full" style={{ backgroundColor: "#0090e3" }} /> Zone 2
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-full" style={{ backgroundColor: "#1fa34a" }} /> Zone 3
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-full" style={{ backgroundColor: "#f15656" }} /> Zone 4
        </span>
      </div>
    </div>
  );
}
